import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Changing this value (e.g. the route path) clears a previous error. */
  resetKey?: string;
}

interface ErrorBoundaryState {
  error: Error | null;
}

/**
 * Catches render-time errors in a route subtree so a single broken page
 * degrades into a friendly message instead of blanking the whole app.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // Surface for diagnostics; a production build would forward this to telemetry.
    console.error('Render error:', error, info.componentStack);
  }

  override componentDidUpdate(prevProps: ErrorBoundaryProps) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  override render() {
    if (this.state.error) {
      return (
        <div className="flex min-h-[60vh] items-center justify-center p-6">
          <div className="w-full max-w-md rounded-2xl border border-border bg-card p-6 text-center shadow-card">
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-red-50 text-red-600 dark:bg-red-400/10 dark:text-red-300">
              <AlertTriangle className="h-6 w-6" />
            </span>
            <h2 className="mt-4 text-lg font-semibold">Terjadi kesalahan pada halaman ini</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Halaman gagal ditampilkan. Coba muat ulang — jika masih terjadi, hubungi administrator.
            </p>
            <div className="mt-5 flex justify-center gap-2">
              <Button variant="outline" onClick={() => this.setState({ error: null })}>
                <RotateCcw className="h-4 w-4" /> Coba lagi
              </Button>
              <Button onClick={() => window.location.reload()}>Muat ulang</Button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
